import {
  connect,
  MapDispatchToPropsParam,
  MapStateToPropsParam,
} from 'react-redux'
import { useTranslation } from 'react-i18next'
import { sizes } from '@lmc/common/constants'
import { NoDataFound } from '../../../components/generic/NoDataFound'
import { FC, useEffect, useState } from 'react'
import Operations from '../../../operations'
import {
  CarerRole,
  Home,
  Need,
  Skill,
  SkillCategory,
  SkillNeedTemplate,
} from '@lmc/ts-common'
import {
  DeleteSkillNeedModal,
  SkillNeedTemplateHeaderSection,
  SkillNeedTemplateModal,
  SkillNeedTemplateTable,
} from './components'
import { Text } from '@lmc/ui-kit'
import { SkillNeedTemplateResponse } from 'screens/Organisation/screens/TrainingMatrixScreen/components/DueOverdueTrainingMatrix'
import { getNeedsAndSkillsTemplates } from 'selectors/NeedsAndSkills'

interface StateProps {
  skillOptions?: Skill[]
  needOptions?: Need[]
  categoriesOptions?: SkillCategory[]
  skillNeedTemplates?: SkillNeedTemplate[]
  preferredPeopleNominative?: string
  carePlanNominative?: string
  role?: string
  home?: Home
  homeGroup?: any
}

interface DispatchProps {
  fetchSkillOptions?: () => Promise<unknown>
  fetchCategoriesOptions?: () => Promise<unknown>
  fetchNeedsOptions?: () => Promise<unknown>
  fetchSkillNeedTemplates?: () => Promise<SkillNeedTemplateResponse[]>
  addSkillNeedTemplate?: (skill: SkillNeedTemplate) => Promise<unknown>
  updateSkillNeedTemplate?: (
    skill: SkillNeedTemplate,
    id: string
  ) => Promise<unknown>
  deleteSkillNeedTemplate?: (id: string) => Promise<unknown>
}

type Props = StateProps & DispatchProps

export const SkillNeedTemplateScreen: FC<Props> = ({
  fetchSkillOptions,
  fetchCategoriesOptions,
  fetchNeedsOptions,
  fetchSkillNeedTemplates,
  skillNeedTemplates,
  addSkillNeedTemplate,
  updateSkillNeedTemplate,
  deleteSkillNeedTemplate,
  skillOptions = [],
  needOptions = [],
  categoriesOptions = [],
  preferredPeopleNominative,
  carePlanNominative,
  role,
  home,
  homeGroup,
}) => {
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)

  const [openDelete, setOpenDelete] = useState(false)

  const [templateDetail, setTemplateDetail] = useState<SkillNeedTemplate>()

  const handleToggleModal = () => {
    if (open) {
      setTemplateDetail(undefined)
    }
    setOpen(!open)
  }

  const handleToogleDeleteModal = () => {
    setOpenDelete(!openDelete)
  }

  const handleRefetchOptions = () => {
    fetchSkillOptions?.()
    fetchCategoriesOptions?.()
    fetchNeedsOptions?.()
    fetchSkillNeedTemplates?.()
  }

  useEffect(() => {
    fetchSkillNeedTemplates?.()
    handleRefetchOptions()
  }, [])

  const confirmDelete = async () => {
    if (templateDetail?.id) {
      await deleteSkillNeedTemplate?.(templateDetail.id)
    }
    handleToogleDeleteModal()
    handleToggleModal()
    setTemplateDetail(undefined)
    fetchSkillNeedTemplates?.()
    handleRefetchOptions()
  }

  const hasSkillsNeedsTemplates = !!skillNeedTemplates?.length

  const createdNeedsIds = skillNeedTemplates
    ?.map((skillNeedTemplate) => skillNeedTemplate.needs)
    .flat()

  const createdSkillIds = skillNeedTemplates?.map(
    (skillNeedTemplate) => skillNeedTemplate.skill
  )

  const isHomeGroup = home?.homeGroup || homeGroup?.id

  const isGroupRole =
    role &&
    [CarerRole.groupAdmin, CarerRole.groupManager].includes(role as CarerRole)

  const isHomeAdminRole = role === CarerRole.careHomeAdmin

  const allowCreateEditTemplates =
    (isHomeGroup && isGroupRole) || (!isHomeGroup && isHomeAdminRole)

  return (
    <>
      <DeleteSkillNeedModal
        isModalVisible={openDelete}
        handleToogleDeleteModal={handleToogleDeleteModal}
        onConfirm={confirmDelete}
        title={t('skillsAndNeeds.areYouSure')}
      >
        <Text as="p2">{t('skillsAndNeeds.byDeleting')}</Text>
        <Text as="p2" style={styles.marginTop}>
          {t('skillsAndNeeds.skillsRemoved', {
            variable: preferredPeopleNominative,
          })}
        </Text>
        <Text as="p2">
          {t('skillsAndNeeds.needsRemoved', {
            carePlansVariable: carePlanNominative,
            carersProfileVariable: preferredPeopleNominative,
          })}
        </Text>
      </DeleteSkillNeedModal>
      <SkillNeedTemplateModal
        isModalVisible={open}
        handleToggleModal={handleToggleModal}
        handleToogleDeleteModal={handleToogleDeleteModal}
        addSkillNeedTemplate={addSkillNeedTemplate}
        updateSkillNeedTemplate={updateSkillNeedTemplate}
        categoriesOptions={categoriesOptions}
        needOptions={needOptions}
        skillOptions={skillOptions}
        preferredPeopleNominative={preferredPeopleNominative}
        skillNeedTemplate={templateDetail}
        fetchCategoriesOptions={fetchCategoriesOptions}
        fetchSkillOptions={fetchSkillOptions}
        fetchNeedsOptions={fetchNeedsOptions}
        createdNeedsIds={createdNeedsIds as string[]}
        createdSkillIds={createdSkillIds as string[]}
      />
      <div style={styles.container}>
        <div style={styles.marginBottom}>
          <SkillNeedTemplateHeaderSection
            handleToggleModal={handleToggleModal}
            allowCreateEditTemplates={allowCreateEditTemplates}
          />
        </div>
        {!hasSkillsNeedsTemplates ? (
          <NoDataFound
            heading={t(`skillsAndNeeds.nothingToSee`)}
            text={t(`skillsAndNeeds.addSkill`)}
            linkText={t(`skillsAndNeeds.findOutMore`)}
            url="https://support.logmycare.co.uk/en/articles/6987442-how-to-set-up-skills-for-your-service"
          />
        ) : (
          <div style={styles.tableContainer}>
            <SkillNeedTemplateTable
              skillOptions={skillOptions}
              needOptions={needOptions}
              skillNeedTemplates={skillNeedTemplates}
              categoriesOptions={categoriesOptions}
              setTemplateDetail={setTemplateDetail}
              allowCreateEditTemplates={allowCreateEditTemplates}
            />
          </div>
        )}
      </div>
    </>
  )
}

const styles = {
  container: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    padding: '24px 24px',
  },
  tableContainer: { overflow: 'auto' },
  marginBottom: { marginBottom: 10 },
  marginTop: { marginTop: 10 },
}

const mapStateToProps = (state: {
  data: {
    skills: Skill[]
    ['skill-categories']: SkillCategory[]
    needs: Need[]
    ['skill-need-templates']: SkillNeedTemplate[]
  }
  global: { home: Home; user: { role: string }; homeGroup?: any }
}) => ({
  skillOptions: state.data?.skills,
  categoriesOptions: state.data?.['skill-categories'],
  needOptions: state.data?.needs,
  skillNeedTemplates: getNeedsAndSkillsTemplates(state),
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  carePlanNominative: state.global.home.preferredCarePlansNominative,
  role: state.global.user.role,
  home: state.global.home,
  homeGroup: state.global?.homeGroup,
})

const mapDispatchToProps = (dispatch: (arg0: unknown) => unknown) => ({
  fetchSkillOptions: () => dispatch(Operations.loadList('skills')),
  fetchCategoriesOptions: () =>
    dispatch(Operations.loadList('skill-categories')),
  fetchNeedsOptions: () => dispatch(Operations.loadList('needs')),
  fetchSkillNeedTemplates: () =>
    dispatch(Operations.loadList('skill-need-templates')),
  addSkillNeedTemplate: (skill: SkillNeedTemplate) =>
    dispatch(
      Operations.createListItem({
        listId: 'skill-need-templates',
        itemId: '',
        json: skill,
      })
    ),
  updateSkillNeedTemplate: (skill: SkillNeedTemplate, id: string) =>
    dispatch(
      Operations.updateListItem({
        listId: 'skill-need-templates',
        itemId: id,
        json: skill,
      })
    ),
  deleteSkillNeedTemplate: (id: string) =>
    dispatch(
      Operations.deleteListItem({
        listId: 'skill-need-templates',
        itemId: id,
      })
    ),
})

export default connect<StateProps, DispatchProps, Props>(
  mapStateToProps as MapStateToPropsParam<StateProps, Props, unknown>,
  mapDispatchToProps as MapDispatchToPropsParam<DispatchProps, Props>
)(SkillNeedTemplateScreen)
